import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  Text,
  InputGroup,
  InputRightElement,
  Spinner,
  Image,
  Select
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { FaCheck } from "react-icons/fa";
import axios from "axios";
import usePlacesAutocomplete from 'use-places-autocomplete';
import { servicebookingFlowStore } from "../../store/ServiceBookingFlowStore";
import { useAuth } from '../../services/AuthContext';
import { authStore } from "../../store/AuthStore";
import { useAlert } from '../AlertProvider';
import MonthYearSelect from '../MonthYearSelect';
// import { put} from "../../lib/api";


// Adjust the image path to where your Medicare sample card is stored
const MedicareCardImage = "/medicare1.png";


const Step10_AddressMedicare: React.FC = () => {
  const { control, handleSubmit, watch, setValue, getValues } = useForm({
    defaultValues: {
      address: "",
      suburb: "",
      postalCode: "",
      medicareNumber: "",
      lineNumber: "",
      expiryDate: "",
    },
  });

  // eslint-disable-next-line
  const [validationError, setValidationError] = useState("");
  const [isAddressValidated, setIsAddressValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMedicareStep, setShowMedicareStep] = useState(false);
  // eslint-disable-next-line
  const formValues = watch();
  // eslint-disable-next-line
  const [showManualAddress, setShowManualAddress] = useState(false);
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
  // eslint-disable-next-line
  const [hasMedicare, setHasMedicare] = useState(true);

  const [isSubmittingMedicare, setIsSubmittingMedicare] = useState(false);



  const { showAlert: showPopUpAlert } = useAlert();

  const { user } = useAuth();
  // eslint-disable-next-line
  const payload = {
    username: localStorage.getItem("accessToken"),
    email: user?.email,

  };

  const medicareNumber = watch("medicareNumber");
  const lineNumber = watch("lineNumber");
  const expiryDate = watch("expiryDate");

  const isMedicareValid = !!medicareNumber && !!lineNumber && !!expiryDate;


  const {
    ready,
    value: addressValue,
    suggestions: { status, data },
    setValue: setAddressValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'au' },
      types: ['address'],
    },
  });

  const handleAddressInput = async (address: string) => {
    setValidationError("");
    setIsAddressValidated(false);

    if (address.length >= 3) {
      setLoading(true);
      try {
        const response = await axios.post(
          "https://api.staging.app.familydoctor.com.au/location/validate",
          { address }
        );

        const result = response.data.result;
        const components = result?.address?.addressComponents || [];

        const hasConfirmedRoute = components.some(
          (c) => c.componentType === "route" && c.confirmationLevel === "CONFIRMED"
        );
        const hasConfirmedLocality = components.some(
          (c) => c.componentType === "locality" && c.confirmationLevel === "CONFIRMED"
        );
        const hasConfirmedPostal = components.some(
          (c) => c.componentType === "postal_code" && c.confirmationLevel === "CONFIRMED"
        );

        if (hasConfirmedRoute && hasConfirmedLocality && hasConfirmedPostal) {
          const suburb = components.find((c) => c.componentType === "locality")?.componentName.text;
          const postal = components.find((c) => c.componentType === "postal_code")?.componentName.text;

          setValue("address", result?.address?.postalAddress?.addressLines[0] || "");
          setValue("suburb", suburb || "");
          setValue("postalCode", postal || "");
          setIsAddressValidated(true);
        } else {
          setValidationError("Invalid address. Please try again.");
        }
      } catch (err) {
        console.error(err);
        setValidationError("Failed to validate address.");
      } finally {
        setLoading(false);
      }
    }
  };


  // const handleBack = () => {
  //   if (showMedicareStep) {
  //     setShowMedicareStep(false);
  //   } else {
  //     bookingFlowStore.goToStep(9);
  //     bookingFlowStore.setActiveStep(9);
  //   }
  // };

  const handleNextStep = async () => {
    setIsSubmittingAddress(true);
    const data = getValues();
    const success = await saveAddressDetails(data);
    setIsSubmittingAddress(false);
  
    if (success) {
      setShowMedicareStep(true);
    }
  };

  const saveAddressDetails = async (data: any) => {
    try {
      const payload = {
        patient_template: {
          Address1: data.address,
          City: data.suburb,
          Postcode: data.postalCode,
        },
      };
  
      const accessToken = await authStore.getAccessToken();
      await axios.put("https://ai.staging.app.familydoctor.com.au/user", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return true;
    } catch (error) {
      showPopUpAlert({
        severity: "error",
        title: "Address Save Failed",
        message: error?.response?.data?.message || error?.message || "Something went wrong",
      });
      return false;
    }
  };
  
  const saveMedicareDetails = async (data: any) => {
    try {
      const { medicareNumber, lineNumber, expiryDate } = data;
  
      const payload = {
        patient_template: {
          MedicareNo: medicareNumber,
          MedicareLineNo: lineNumber,
          MedicareExpiry: `${expiryDate.split("-")[1]}/${expiryDate.split("-")[0]}`,
        },
      };
  
      const accessToken = await authStore.getAccessToken();
      await axios.put("https://ai.staging.app.familydoctor.com.au/user", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
  
      servicebookingFlowStore.setActiveStep(3);
      servicebookingFlowStore.goToConformation();
    } catch (error) {
      showPopUpAlert({
        severity: "error",
        title: "Medicare Save Failed",
        message: error?.response?.data?.message || error?.message || "Something went wrong",
      });
    }
  };
  

  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center" width="310px" px={2} py={6} >
      <Box minW="350px" width="100%" px={4}>
        <form  style={{ width: "100%" }}>
          <Text textAlign="center" fontSize="lg" fontWeight="medium" mb={6}>
            Please enter these details to register as a new patient.
          </Text>

          {!showMedicareStep && (
            <>
              <FormControl mb={4} >
                {/* <FormLabel>Patient Address</FormLabel> */}
                <Box position="relative">
                  <InputGroup>
                    <Input
                      value={addressValue}
                      onChange={(e) => {
                        setAddressValue(e.target.value);
                        setValue("address", e.target.value);
                      }}
                      onBlur={() => handleAddressInput(addressValue)}
                      placeholder="Please enter your address"
                      borderRadius={30}
                      disabled={!ready}
                    />
                    <InputRightElement>
                      {loading ? <Spinner size="sm" color="blue.500" /> : isAddressValidated && <FaCheck color="green" />}
                    </InputRightElement>
                  </InputGroup>
                  {(status === 'OK' || !showManualAddress) && (
                    <>
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        bg="white"
                        zIndex={10}
                        boxShadow="md"
                        borderRadius="md"
                        mt={1}
                        maxHeight="200px"
                        overflowY="auto"
                      >
                        {status === 'OK' && !showManualAddress && data.map(({ place_id, description }) => (
                          <Box
                            key={place_id}
                            px={4}
                            py={2}
                            _hover={{ backgroundColor: 'gray.100', cursor: 'pointer' }}
                            onClick={async () => {
                              setAddressValue(description, false);
                              clearSuggestions();
                              setValue("address", description);
                              await handleAddressInput(description);
                            }}
                          >
                            {description}
                          </Box>
                        ))}



                      </Box>
                      {/* Fallback button */}
                      {/* <Text
                        textAlign="right"
                        fontSize="sm"
                        fontWeight="normal"
                        color={servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg}
                        cursor="pointer"
                        onClick={() => {
                          clearSuggestions();
                          setShowManualAddress(true);
                        }}
                      >
                        My address is not there
                      </Text> */}
                    </>
                  )}

                  {showManualAddress && (
                    <>
                      <FormControl mt={4}>
                        {/* <FormLabel>Suburb</FormLabel> */}
                        <Controller
                          name="suburb"
                          control={control}
                          rules={{ required: "Suburb is required" }}
                          render={({ field }) => (
                            <Input {...field} placeholder="Enter suburb" borderRadius={30} />
                          )}
                        />
                      </FormControl>

                      <FormControl mt={4}>
                        {/* <FormLabel>Postal Code</FormLabel> */}
                        <Controller
                          name="postalCode"
                          control={control}
                          rules={{ required: "Postal code is required" }}
                          render={({ field }) => (
                            <Input {...field} placeholder="Enter postal code" borderRadius={30} />
                          )}
                        />
                      </FormControl>
                    </>
                  )}


                </Box>
              </FormControl>

              <Box gap={4} justifyContent="center" mt={5}>
                {/* <Button
                  variant="outline"
                  color="#B92F2F"
                  borderColor="#B92F2F"
                  onClick={handleBack}
                  _hover={{ bg: "#B92F2F", color: "white" }}
                  borderRadius={30}
                  minW="140px"
                >
                  Back */}
                {/* </Button> */}

                {/* {!showManualAddress && (<Button
                  variant="outline"
                  type="submit"
                  borderColor={servicebookingFlowStore?.configurationDetails?.colorScheme?.inputBorder}
                  color={servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg}
                  border="2px"
                  borderRadius={30}
                  w="100%"
                  onClick={() => {
                    clearSuggestions();
                    setShowManualAddress(true);
                  }}
                >
                  My address is not there
                </Button>)} */}
                <Button
                  mt={3}
                  bg={servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg}
                  color="white"
                  onClick={handleNextStep}
                  isDisabled={!isAddressValidated && !showManualAddress}
                  isLoading={isSubmittingAddress}
                  borderRadius={30}
                  w="100%"
                >
                  Next
                </Button>
              </Box>
            </>
          )}

          {showMedicareStep && (
            <>
              <Box display="flex" justifyContent="center" mb={4}>
                <Image
                  src={MedicareCardImage}
                  alt="Medicare sample"
                  borderRadius="md"
                  width="100%"
                  maxW="380px"
                />
              </Box>

              <FormControl mb={4}>
                <Text fontSize="sm" mb={1}>Enter Medicare Number and Line Number</Text>

                <Box display="flex" gap={4} flexDirection={{ base: 'column', md: 'row' }}>
                  {/* Medicare Number Input */}
                  <Box flex="1">
                    <Controller
                      name="medicareNumber"
                      control={control}
                      rules={
                        hasMedicare
                          ? {
                            required: "Medicare number is required",
                            pattern: {
                              value: /^\d{10}$/,
                              message: "Must be exactly 10 digits",
                            },
                          }
                          : {}
                      }

                      render={({ field, fieldState }) => (
                        <>
                          <Input
                            {...field}
                            placeholder="10 digit Medicare Number"
                            borderRadius={30}
                            maxLength={10}
                            inputMode="numeric"
                          />
                          {hasMedicare && fieldState.error && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                              {fieldState.error.message}
                            </Text>
                          )}
                        </>
                      )}
                    />
                  </Box>

                  {/* Line Number Dropdown */}
                  <Box width={{ base: "100%", md: "120px" }}>
                    <Controller
                      name="lineNumber"
                      control={control}
                      rules={
                        hasMedicare
                          ? {
                            required: "Line number is required",
                            pattern: {
                              value: /^[1-9]{1}$/,
                              message: "Must be a single digit (1-9)",
                            },
                          }
                          : {}
                      }
                      render={({ field, fieldState }) => (
                        <>
                          <Select
                            {...field}
                            placeholder="Line"
                            borderRadius={30}
                          >
                            {Array.from({ length: 9 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {i + 1}
                              </option>
                            ))}
                          </Select>
                          {hasMedicare && fieldState.error && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                              {fieldState.error.message}
                            </Text>
                          )}
                        </>
                      )}
                    />
                  </Box>
                </Box>
              </FormControl>
              <Text fontSize="sm" mb={1}>Select expiry date</Text>
              <FormControl mb={4}>
                <Controller
                  name="expiryDate"
                  control={control}
                  rules={hasMedicare ? { required: "Expiry date is required" } : {}}
                  render={({ field }) => (
                    <MonthYearSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FormControl>

              <Box gap={5} justifyContent="center" mt={6}>
                
                <Button
                  isLoading={isSubmittingMedicare}
                  variant="outline"
                  borderColor={servicebookingFlowStore?.configurationDetails?.colorScheme?.inputBorder}
                  color={servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg}
                  border="2px"
                  borderRadius={30}
                  w="100%"
                  onClick={() => {
                    servicebookingFlowStore.setActiveStep(3);
                    servicebookingFlowStore.goToConformation();
                  }}
                >
                  I don't have Medicare, Go to next Step
                </Button>
                <Button
                  mt={3}
                  bg={servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg}
                  color="white"
                  isLoading={isSubmittingMedicare}
                  isDisabled={!isMedicareValid}
                  onClick={handleSubmit(async (data) => {
                    setIsSubmittingMedicare(true);
                    await saveMedicareDetails(data);
                    setIsSubmittingMedicare(false);
                  })}
                  type="submit"
                  borderRadius={30}
                  w="100%"
                >
                  Submit
                </Button>
              </Box>
            </>
          )}
        </form>
      </Box>
    </Flex>
  );
};

export default Step10_AddressMedicare;
